/* Web Push subscription helper. */
import api from "@/lib/api";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function registerServiceWorker() {
  if (!isPushSupported()) return null;
  try {
    const reg =
      (await navigator.serviceWorker.getRegistration("/sw.js")) ||
      (await navigator.serviceWorker.register("/sw.js"));
    return reg;
  } catch (e) {
    console.warn("SW registration failed", e);
    return null;
  }
}

export async function requestPushPermissionAndSubscribe() {
  if (!isPushSupported()) throw new Error("Push nie jest wspierany w tej przeglądarce.");
  const reg = await registerServiceWorker();
  if (!reg) throw new Error("Nie udało się zarejestrować Service Workera.");

  // Native browsers only show the permission prompt when state is "default".
  // If the user previously denied, requestPermission() returns "denied"
  // *without* re-prompting — so we surface a helpful, actionable error.
  const current = typeof Notification !== "undefined" ? Notification.permission : "default";
  if (current === "denied") {
    const err = new Error(
      "Powiadomienia zostały zablokowane w przeglądarce. Otwórz ustawienia strony (ikona kłódki obok adresu) → Powiadomienia → Zezwól, a następnie odśwież stronę."
    );
    err.code = "denied";
    throw err;
  }
  if (current === "default") {
    const perm = await Notification.requestPermission();
    if (perm !== "granted") {
      const err = new Error(
        'Klikając "Blokuj" nie otrzymasz powiadomień. Możesz to zmienić w ustawieniach strony (ikona kłódki obok adresu).'
      );
      err.code = "denied";
      throw err;
    }
  }
  // current === "granted" (or just switched to granted): proceed with subscribe.

  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    await sendSubscriptionToServer(existing);
    return existing;
  }

  const { data } = await api.get("/push/vapid-public-key");
  const key = data?.key;
  if (!key) throw new Error("Brak konfiguracji VAPID na serwerze.");

  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(key),
  });
  await sendSubscriptionToServer(sub);
  return sub;
}

async function sendSubscriptionToServer(sub) {
  const json = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: json.endpoint,
    keys: json.keys,
    user_agent: navigator.userAgent,
  });
}

export async function hasActivePushSubscription() {
  if (!isPushSupported()) return false;
  const reg = await navigator.serviceWorker.getRegistration("/sw.js");
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  return !!sub;
}

export function getBrowserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Warsaw";
  } catch {
    return "Europe/Warsaw";
  }
}
